import type {
  ReactNode} from "react";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { gGateway } from "@utils/gateway";

import type { User } from "../../../models/User";
import type { ApiClient } from "../../../utils/api/_makeApiClient";

import { useApiClient } from "./useApiClient";

const UserContext = createContext<User | undefined | null>(null);

export function useUser(): User | undefined | null {
  return useContext(UserContext);
}

export function useAssertedUser(): User | undefined {
  const user = useContext(UserContext);

  if (user === null) {
    throw new Error("useAssertedUser(): SSO hasn't finished running yet");
  }

  return user;
}

export function UserProvider({
  children,
}: {
  children: React.ReactNode;
}): ReactNode {
  const apiClient = useApiClient();
  const [user, setUser] = useState<User | undefined | null>(null);

  useEffect(() => {
    singleSignOn(apiClient).then((user) => setUser(user));
  }, [apiClient]);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}

async function singleSignOn(apiClient: ApiClient): Promise<User | undefined> {
  const userData = await gGateway.authenticateUser(
    "gu-unic",
    window._wp.ssr.loginConnectorToken,
  );

  if (!userData.loginConnectorToken) {
    return undefined;
  }

  return {
    loginConnectorToken: userData.loginConnectorToken,
    ...(await apiClient.fetchUser(userData.id)),
  };
}
